import { useEffect, useRef, useState } from 'react'

/** True while a modal dialog is open (same check as the undo/redo and map-editing shortcuts). */
export function isModalDialogOpen(): boolean {
  return document.querySelector('[role="dialog"][aria-modal="true"]') != null
}

/** Whether a drag carries files from the OS (not text, links or an in-page element drag). */
export function dragHasFiles(event: DragEvent): boolean {
  const types = event.dataTransfer?.types
  return types != null && Array.from(types).includes('Files')
}

export interface UseWindowFileDropOptions {
  /** Called with the dropped files (never with an empty list). */
  onFiles: (files: FileList) => void
  /**
   * While this returns true, file drags are neither highlighted nor
   * imported (the drop is still swallowed, so the browser doesn't open the
   * file and navigate away). Defaults to "a modal dialog is open".
   */
  isBlocked?: () => boolean
}

/**
 * Makes the whole app window the drop target for OS file drags: window-
 * level `dragenter`/`dragover`/`dragleave`/`drop` listeners, so the area
 * that accepts a drop is exactly the area the overlay highlights (the
 * caller renders one fixed-position overlay while this returns true),
 * wherever the pointer is -- toolbar, sidebar, workspace (scrolled or not)
 * or timeline. Register once, at the app shell.
 *
 * - Drags that don't carry files (selected text, links, an in-page drag)
 *   are ignored entirely: no highlight, default browser behavior kept.
 * - `dragenter`/`dragleave` fire for every child element crossed, so the
 *   highlight is driven by an enter/leave counter rather than by the
 *   latest event; it ends when the counter returns to zero (the pointer
 *   left the window) or on drop.
 * - `dragover` and `drop` always `preventDefault()` for file drags: the
 *   first is what makes the window a valid drop target, the second stops
 *   the browser from opening the dropped file in place of the app.
 * - While `isBlocked()` (a modal dialog is open) nothing is highlighted
 *   or imported, and the cursor shows the drop isn't accepted.
 */
export function useWindowFileDrop({
  onFiles,
  isBlocked = isModalDialogOpen,
}: UseWindowFileDropOptions): boolean {
  const [isDragActive, setIsDragActive] = useState(false)
  // Latest callbacks, so the listeners are bound once.
  const latest = useRef({ onFiles, isBlocked })
  useEffect(() => {
    latest.current = { onFiles, isBlocked }
  }, [onFiles, isBlocked])

  useEffect(() => {
    let depth = 0

    const reset = () => {
      depth = 0
      setIsDragActive(false)
    }

    const onDragEnter = (event: DragEvent) => {
      if (!dragHasFiles(event)) return
      event.preventDefault()
      depth += 1
      setIsDragActive(!latest.current.isBlocked())
    }

    const onDragOver = (event: DragEvent) => {
      if (!dragHasFiles(event)) return
      event.preventDefault()
      const blocked = latest.current.isBlocked()
      if (event.dataTransfer != null) event.dataTransfer.dropEffect = blocked ? 'none' : 'copy'
      // A drag that entered before this listener existed (or whose
      // dragenter was missed) still lights the overlay up.
      if (depth === 0) depth = 1
      setIsDragActive(!blocked)
    }

    const onDragLeave = (event: DragEvent) => {
      if (!dragHasFiles(event)) return
      depth = Math.max(0, depth - 1)
      if (depth === 0) setIsDragActive(false)
    }

    const onDrop = (event: DragEvent) => {
      if (!dragHasFiles(event)) return
      event.preventDefault()
      reset()
      if (latest.current.isBlocked()) return
      const files = event.dataTransfer?.files
      if (files != null && files.length > 0) latest.current.onFiles(files)
    }

    window.addEventListener('dragenter', onDragEnter)
    window.addEventListener('dragover', onDragOver)
    window.addEventListener('dragleave', onDragLeave)
    window.addEventListener('drop', onDrop)
    return () => {
      window.removeEventListener('dragenter', onDragEnter)
      window.removeEventListener('dragover', onDragOver)
      window.removeEventListener('dragleave', onDragLeave)
      window.removeEventListener('drop', onDrop)
    }
  }, [])

  return isDragActive
}
